import React, {ReactNode, useMemo} from 'react'
import styled from 'styled-components'
import {Icon, Modal, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {translate, TranslationKeyGiftShop} from 'i18n'
import {parseShape} from 'types'
import {getFontFamily, getFontSize} from 'utils'
import {useTheme} from 'themes'

const StyledModal = styled(Modal)`
  ${({theme}) => ({backgroundColor: theme.black_1})}
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${convertUnit(325)};
  padding: ${convertUnit(25)};
  margin: ${convertUnit(25)};
`

interface GiftshopPackageInfoModalProps {
  visible: boolean
  duration: string
  bottomElement: ReactNode
  onClose: () => void
}

export default function GiftshopPackageInfoModal({
  visible,
  duration,
  bottomElement,
  onClose,
}: GiftshopPackageInfoModalProps) {
  const theme = useTheme()

  const tutorialKeys = useMemo<TranslationKeyGiftShop[]>(
    () => [
      'giftShop:packageDetailModalTutorial_1',
      'giftShop:packageDetailModalTutorial_2',
      'giftShop:packageDetailModalTutorial_3',
      'giftShop:packageDetailModalTutorial_4',
      'giftShop:packageDetailModalTutorial_5',
    ],
    [],
  )

  const handleRenderText: parseShape[] = useMemo(
    () => [
      {
        pattern: /(<b>.*?<\/b>)/,
        style: {
          fontSize: getFontSize('m'),
          fontFamily: getFontFamily('bold'),
        },
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
    ],
    [],
  )

  const handleRenderInfo = useMemo(
    () => (
      <div
        style={{
          marginBottom: convertUnit(16),
        }}>
        <Paragraph color="white_1" fontSize="xl" fontWeight="bold">
          {translate('giftShop:packageDetailModalInfo')}
        </Paragraph>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: convertUnit(4),
            alignItems: 'center',
          }}>
          <Paragraph color="white_1">{translate('global:duration')}</Paragraph>
          <Icon type="clock-ol" color="warning_5" />
          <Paragraph color="warning_5">{duration}</Paragraph>
        </div>
      </div>
    ),
    [duration],
  )

  const handleRenderTutorial = useMemo(
    () => (
      <div>
        <Paragraph fontSize="xl" fontWeight="bold" color="white_1">
          {translate('giftShop:packageDetailModalTutorial')}
        </Paragraph>
        <div
          style={{
            marginBottom: convertUnit(24),
          }}>
          {tutorialKeys.map((key, index) => (
            <div
              key={key}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                gap: convertUnit(2),
              }}>
              <Paragraph fontSize="m" color="white_1" lineHeight={26}>
                {index + 1}.
              </Paragraph>
              <ParsedText
                style={{
                  fontFamily: getFontFamily('regular'),
                  fontSize: getFontSize('m'),
                  color: theme.white_1,
                  lineHeight: convertUnit(26),
                }}
                parse={handleRenderText}>
                {translate(key)}
              </ParsedText>
            </div>
          ))}
        </div>
        <div
          style={{
            backgroundColor: theme.black_2,
            display: 'flex',
            flexDirection: 'column',
            padding: `${convertUnit(8)} ${convertUnit(12)}`,
            marginBottom: convertUnit(24),
            borderRadius: convertUnit(8),
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: convertUnit(8),
            }}>
            <Paragraph fontSize="m" color="white_1">
              {translate('giftShop:packageDetailModalTutorial_6_1')}
            </Paragraph>
            <div
              style={{
                backgroundColor: theme.danger_5,
                display: 'flex',
                flexDirection: 'row',
                borderBottomLeftRadius: convertUnit(8),
                gap: convertUnit(2),
                padding: convertUnit(4),
                width: 'auto',
              }}>
              <Icon size={16} type="cart" color="white_1" />
              <Icon size={16} type="clock-ol" color="white_1" />
            </div>
          </div>
          <Paragraph fontSize="m" fontWeight="medium" color="white_1">
            {translate('giftShop:packageDetailModalTutorial_6_2')}
          </Paragraph>
        </div>
      </div>
    ),
    [
      handleRenderText,
      theme.black_2,
      theme.danger_5,
      theme.white_1,
      tutorialKeys,
    ],
  )

  return (
    <StyledModal visible={visible} onBackdropPress={onClose}>
      {handleRenderInfo}
      {handleRenderTutorial}
      {bottomElement}
    </StyledModal>
  )
}
